import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { translateUiText } from "./translateUiText";

const translatedNodes = new WeakMap();
const translatedAttributes = new WeakMap();
const translatableAttributes = ["placeholder", "title", "aria-label", "alt"];

const translateTextNode = (node) => {
  const previous = translatedNodes.get(node);
  const current = node.nodeValue;
  const source = previous && current === previous.output ? previous.source : current;
  const output = translateUiText(source);
  translatedNodes.set(node, { source, output });
  if (current !== output) node.nodeValue = output;
};

const translateElementAttributes = (element) => {
  if (!(element instanceof Element)) return;
  const saved = translatedAttributes.get(element) || {};
  translatableAttributes.forEach((attribute) => {
    if (!element.hasAttribute(attribute)) return;
    const current = element.getAttribute(attribute);
    const previous = saved[attribute];
    const source = previous && current === previous.output ? previous.source : current;
    const output = translateUiText(source);
    saved[attribute] = { source, output };
    if (current !== output) element.setAttribute(attribute, output);
  });
  translatedAttributes.set(element, saved);
};

const translateTree = (root) => {
  if (!root) return;
  if (root.nodeType === Node.TEXT_NODE) translateTextNode(root);
  if (root.nodeType === Node.ELEMENT_NODE) translateElementAttributes(root);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) translateTextNode(node);
    else translateElementAttributes(node);
    node = walker.nextNode();
  }
};

const LocalizationBoundary = ({ children }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    let translating = false;
    const apply = (root = document.body) => {
      if (translating) return;
      translating = true;
      observer.disconnect();
      translateTree(root);
      observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: translatableAttributes });
      translating = false;
    };
    const observer = new MutationObserver((mutations) => {
      if (translating) return;
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") mutation.addedNodes.forEach(apply);
        else apply(mutation.target);
      });
    });
    apply();
    return () => observer.disconnect();
  }, [i18n.resolvedLanguage]);

  return children;
};

export default LocalizationBoundary;
